import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cbomApi, sbomApi, sourcesApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { QuantumSafeBadge } from '@/components/common/StatusBadge'
import VexPanel from '@/components/common/VexPanel'
import type { QuantumRisk, Severity, PackageFinding, PackageSummary } from '@/api/types'

const PAGE_SIZE = 50

const RISK_LEVELS: QuantumRisk[] = ['vulnerable', 'partial', 'hybrid', 'safe', 'unknown']

const RISK_COLORS: Record<QuantumRisk, string> = {
  vulnerable: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  partial:    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  hybrid:     'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  safe:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  unknown:    'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark',
}

export function RiskBadge({ risk }: { risk: QuantumRisk }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium capitalize ${RISK_COLORS[risk] ?? RISK_COLORS.unknown}`}>
      {risk || 'unknown'}
    </span>
  )
}

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'bg-error/15 text-error dark:bg-error/25 dark:text-red-300',
  high:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium:   'bg-warning/15 text-yellow-800 dark:bg-warning/20 dark:text-yellow-300',
  low:      'bg-success/10 text-green-800 dark:bg-success/15 dark:text-green-300',
  info:     'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark',
}

export function SeverityBadge({ level }: { level: Severity }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${SEVERITY_COLORS[level] ?? SEVERITY_COLORS.info}`}>
      {level}
    </span>
  )
}


export default function Sbom() {
  const { currentOrgId } = useOrgStore()
  const [pkgSearch, setPkgSearch] = useState('')
  const [pkgQRisk, setPkgQRisk] = useState<QuantumRisk | ''>('')
  const [pkgEcosystem, setPkgEcosystem] = useState('')
  const [pkgScope, setPkgScope] = useState<'all' | 'direct' | 'transitive'>('all')
  const [pkgSafety, setPkgSafety] = useState<'all' | 'safe' | 'unsafe'>('all')
  const [pkgSeverity, setPkgSeverity] = useState<Severity | ''>('')
  const [pkgAlgorithm, setPkgAlgorithm] = useState('')
  const [expandedPkgId, setExpandedPkgId] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)

  const [pages, setPages] = useState<PackageFinding[][]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<PackageSummary | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    setOffset(0)
    setHasMore(false)
  }, [pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety, pkgAlgorithm])

  const { isLoading, isFetching, isError, error, data: pkgReport } = useQuery({
    queryKey: ['org-pkg-findings', currentOrgId, pkgSearch, pkgQRisk, pkgEcosystem, pkgScope, pkgSafety, pkgAlgorithm, offset],
    queryFn: () => cbomApi.fetchOrgPackageFindings(currentOrgId, {
      limit: PAGE_SIZE,
      offset,
      name: pkgSearch || undefined,
      quantum_risk: pkgQRisk || undefined,
      ecosystem: pkgEcosystem || undefined,
      scope: pkgScope === 'all' ? undefined : pkgScope,
      is_quantum_safe: pkgSafety === 'all' ? undefined : pkgSafety === 'safe',
      algorithm: pkgAlgorithm || undefined,
    }),
    enabled: !!currentOrgId,
  })

  useEffect(() => {
    if (!pkgReport) return
    const batch = pkgReport.findings ?? []
    setPages((prev) => {
      if (offset === 0) return [batch]
      const next = [...prev]
      next[offset / PAGE_SIZE] = batch
      return next
    })
    setHasMore(offset + batch.length < pkgReport.total)
    setTotal(pkgReport.total)
    if (offset === 0) setSummary(pkgReport.summary)
  }, [pkgReport, offset])

  isFetchingRef.current = isFetching

  const loadMore = useCallback(() => setOffset((p) => p + PAGE_SIZE), [])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) loadMore()
      },
      { rootMargin: '600px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  const { data: sourcesResp } = useQuery({
    queryKey: ['sources', currentOrgId],
    queryFn: () => sourcesApi.list(currentOrgId),
    enabled: !!currentOrgId,
  })
  const sourceMap = Object.fromEntries((sourcesResp?.data ?? []).map((s) => [s.id, s.name]))

  const visible = pages.flat().filter(pkg => !pkgSeverity || pkg.severity === pkgSeverity)

  async function handleDownload(format: 'cyclonedx' | 'spdx' | 'swid') {
    setDownloading(format)
    try {
      await sbomApi.downloadOrgSbom(currentOrgId, format)
    } finally {
      setDownloading(null)
    }
  }

  const s = summary
  const safeCount = s?.by_quantum_risk['safe'] ?? 0
  const atRisk = (s?.total ?? 0) - safeCount
  const highCritical = s?.by_quantum_risk['vulnerable'] ?? 0
  const safePercent = s?.total ? Math.round((safeCount / s.total) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Packages</h1>
          {s && (
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
              {safeCount} of {total} quantum safe
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(['cyclonedx', 'spdx', 'swid'] as const).map((fmt) => (
            <button
              key={fmt}
              onClick={() => handleDownload(fmt)}
              disabled={!!downloading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-text-light dark:text-text-dark rounded-lg hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            >
              {downloading === fmt ? (
                <span className="material-icons text-base animate-spin">autorenew</span>
              ) : (
                <span className="material-icons text-base">download</span>
              )}
              {fmt === 'cyclonedx' ? 'CycloneDX' : fmt.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div>
            <p className="text-sm font-medium text-error">Failed to load packages</p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">{(error as Error)?.message ?? 'Unknown error'}</p>
          </div>
        </div>
      )}

      {/* Stat blocks */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
            <p className="label-sm text-subtle-light dark:text-subtle-dark">Total Packages</p>
            <p className="text-2xl font-bold tracking-display text-text-light dark:text-text-dark mt-1">{s.total}</p>
          </div>
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
            <p className="label-sm text-subtle-light dark:text-subtle-dark">Quantum Safe</p>
            <p className="text-2xl font-bold tracking-display text-success mt-1">
              {safeCount}
              <span className="text-sm font-normal text-subtle-light dark:text-subtle-dark ml-1">({safePercent}%)</span>
            </p>
          </div>
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
            <p className="label-sm text-subtle-light dark:text-subtle-dark">At Risk</p>
            <p className="text-2xl font-bold tracking-display text-error mt-1">{atRisk}</p>
          </div>
          <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl">
            <p className="label-sm text-subtle-light dark:text-subtle-dark">Vulnerable</p>
            <p className="text-2xl font-bold tracking-display text-warning mt-1">{highCritical}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      {!isError && (
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search by name or PURL…"
              value={pkgSearch}
              onChange={(e) => setPkgSearch(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-60 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={pkgQRisk}
              onChange={(e) => setPkgQRisk(e.target.value as QuantumRisk | '')}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Quantum Risk</option>
              {RISK_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              value={pkgSeverity}
              onChange={(e) => setPkgSeverity(e.target.value as Severity | '')}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Severities</option>
              {(['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Ecosystem…"
              value={pkgEcosystem}
              onChange={(e) => setPkgEcosystem(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-36 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={pkgScope}
              onChange={(e) => setPkgScope(e.target.value as 'all' | 'direct' | 'transitive')}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="all">All Scope</option>
              <option value="direct">Direct</option>
              <option value="transitive">Transitive</option>
            </select>
            <select
              value={pkgSafety}
              onChange={(e) => setPkgSafety(e.target.value as 'all' | 'safe' | 'unsafe')}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="all">All Quantum Status</option>
              <option value="safe">Safe</option>
              <option value="unsafe">At Risk</option>
            </select>
            <input
              type="text"
              placeholder="Algorithm (e.g. RSA-2048)…"
              value={pkgAlgorithm}
              onChange={(e) => setPkgAlgorithm(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-48 focus:outline-none focus:ring-2 focus:ring-primary/50"
              title="Filter by linked crypto algorithm name"
            />
          </div>
        </div>
      )}

      {/* Loading — initial only */}
      {isLoading && pages.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <span className="material-icons animate-spin text-primary text-3xl">sync</span>
        </div>
      )}

      {/* Empty */}
      {!isFetching && !isError && visible.length === 0 && (
        <div className="text-center py-12">
          <span className="material-icons text-3xl text-subtle-light dark:text-subtle-dark opacity-40">inventory_2</span>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-2">
            {total > 0 ? 'No packages match the current filters.' : 'No packages found.'}
          </p>
        </div>
      )}

      {/* Table */}
      {!isError && visible.length > 0 && (
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-light dark:border-border-dark">
                <th className="w-8 px-3 py-3" />
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Package</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Version</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Ecosystem</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden sm:table-cell">Scope</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Severity</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Risk</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark">Quantum Safety</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden md:table-cell">Evidence</th>
                <th className="text-left px-4 py-3 font-medium text-subtle-light dark:text-subtle-dark hidden lg:table-cell">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {visible.map((pkg, i) => (
                <Fragment key={pkg.id}>
                  <tr
                    className={`cursor-pointer transition-colors hover:bg-background-light dark:hover:bg-background-dark ${i % 2 === 1 ? 'bg-background-light/50 dark:bg-background-dark/50' : ''}`}
                    onClick={() => setExpandedPkgId(prev => prev === pkg.id ? null : pkg.id)}
                  >
                    <td className="px-3 py-3 text-center">
                      <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
                        {expandedPkgId === pkg.id ? 'expand_less' : 'expand_more'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-light dark:text-text-dark truncate max-w-[200px]">{pkg.name}</p>
                      {pkg.purl && (
                        <p className="font-mono text-xs text-subtle-light dark:text-subtle-dark truncate max-w-[200px]" title={pkg.purl}>{pkg.purl}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden sm:table-cell">{pkg.version || '—'}</td>
                    <td className="px-4 py-3 text-subtle-light dark:text-subtle-dark hidden md:table-cell">{pkg.ecosystem || '—'}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                        pkg.is_direct ? 'bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary' : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark'
                      }`}>
                        {pkg.is_direct ? 'Direct' : 'Transitive'}
                      </span>
                    </td>
                    <td className="px-4 py-3"><SeverityBadge level={pkg.severity} /></td>
                    <td className="px-4 py-3"><RiskBadge risk={pkg.quantum_risk} /></td>
                    <td className="px-4 py-3"><QuantumSafeBadge isSafe={pkg.is_quantum_safe} /></td>
                    <td className="px-4 py-3 text-xs text-subtle-light dark:text-subtle-dark hidden md:table-cell max-w-[200px]">
                      <div className="flex flex-col gap-1">
                        {pkg.linked_assets?.length ? (
                          <span className="font-mono" title={pkg.linked_assets.map(e => e.name).join(', ')}>
                            {pkg.linked_assets.length} crypto link{pkg.linked_assets.length !== 1 ? 's' : ''}
                          </span>
                        ) : null}
                        {pkg.evidence?.occurrences?.length ? (
                          <span className="font-mono truncate block" title={pkg.evidence.occurrences[0].location}>{pkg.evidence.occurrences[0].location}</span>
                        ) : null}
                        {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length ? '—' : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs hidden lg:table-cell">
                      {sourceMap[pkg.source_id] ? (
                        <Link
                          to={`/sources/${pkg.source_id}`}
                          className="text-primary hover:underline font-mono"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {sourceMap[pkg.source_id]}
                        </Link>
                      ) : (
                        <span className="font-mono text-subtle-light dark:text-subtle-dark">{pkg.source_id.slice(0, 8)}…</span>
                      )}
                    </td>
                  </tr>
                  {expandedPkgId === pkg.id && (
                    <tr className="bg-container-light dark:bg-container-dark">
                      <td />
                      <td colSpan={9} className="px-4 py-4">
                        <div className="space-y-4">
                          {pkg.linked_assets?.length ? (
                            <VexPanel evidence={pkg.linked_assets} />
                          ) : null}
                          {pkg.evidence?.occurrences?.length ? (
                            <div>
                              <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2">Evidence ({pkg.evidence.occurrences.length})</p>
                              <ul className="space-y-1">
                                {pkg.evidence.occurrences.map((o, j) => (
                                  <li key={j} className="text-xs font-mono bg-background-light dark:bg-background-dark rounded px-3 py-1.5">
                                    <span className="text-primary">{o.location}</span>
                                    {o.additionalContext && <span className="block text-subtle-light dark:text-subtle-dark mt-0.5 break-all">{o.additionalContext}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          {!pkg.linked_assets?.length && !pkg.evidence?.occurrences?.length && (
                            <p className="text-xs text-subtle-light dark:text-subtle-dark">No evidence available.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div ref={sentinelRef} className="h-1" />
      {isFetching && offset > 0 && (
        <div className="flex items-center justify-center py-4">
          <span className="material-icons animate-spin text-primary text-2xl">sync</span>
        </div>
      )}
    </div>
  )
}
